<template>
  <div class="flex-1 flex flex-col items-center">
    <div
      class="top-bar w-full flex items-center">
      <router-link to="/" class="go-home">
        <svg
          class="w-1 h-1 ml-05">
          <use :xlink:href="'#previous'"></use>
        </svg>
      </router-link>
      <div class="flag available" :class="{[lang]: true}"></div>

      <Btn
        class="ml-auto mr-1"
        blueText
        text="Download json"
        @click.native="download"/>

      <Btn
        v-if="dirtySnips.length"
        medium
        success
        :text="`Save (${dirtySnips.length})`"
        class="mr-1"
        icon="ok"
        @click.native="saveDirty"/>
    </div>

    <div class="main-area flex-1 w-full pad-h">
      <div
        v-if="!isLoaded"
        class="flex justify-center pad-v">
        <div class="spinner"></div>
      </div>

      <!-- Still to be translated -->
      <div
        v-if="missing.length && isLoaded"
        class="mt-1">
        <h2>Left to translate: {{ missing.length }}</h2>
        <ProgressBar
          style="height: 1rem; margin-bottom: 3rem"
          :progress="remainingPercent"/>

        <label>
          <div @dblclick="supplyDefault">{{ missing[0]._id }}</div>
          <Field
            ref="singleSnip"
            v-model="missing[0][lang]"
            @enter="saveCurrent"/>
        </label>
        <small v-if="(lang !== 'en') && missing[0].en">EN: {{ missing[0]['en'] }}</small>
        <small v-if="(lang !== 'de') && missing[0].de">DE: {{ missing[0]['de'] }}</small>
        <small v-if="(lang !== 'fr') && missing[0].fr">FR: {{ missing[0]['fr'] }}</small>
        <small v-if="(lang !== 'es') && missing[0].es">ES: {{ missing[0]['es'] }}</small>

        <Btn
          class="mt-025"
          text="Save translation"
          blue-text
          invert
          @click.native="saveCurrent"/>

      </div>

      <input type="checkbox" id="show-translated" ref="showAll">
      <label
        v-if="snips.length"
        class="toggle-translated block tcyan cursor-pointer flex items-center" for="show-translated">
        <svg
          class="w-05 h-05">
          <use :xlink:href="'#previous'"></use>
        </svg>
        See already translated texts
      </label>

      <!-- Already translated -->
      <div class="snips-done">
        <SearchBox
          v-if="snips.length"
          :input-value="searchString"
          placeholder="Search..."
          class="pad-v-half"
          @input="handleSearch"/>

        <div
          class="label-and-value"
          v-for="snip in filteredSnips"
          :key="snip._id">
          <label>
            <div>{{ snip._id }}</div>
            <Field
              v-model="snip[lang]"
              @input="handleChange(snip._id, ...arguments)"/>
          </label>
          <small v-if="(lang !== 'en') && snip.en">EN: {{ snip['en'] }}</small>
          <small v-if="(lang !== 'de') && snip.de">DE: {{ snip['de'] }}</small>
          <small v-if="(lang !== 'fr') && snip.fr">FR: {{ snip['fr'] }}</small>
          <small v-if="(lang !== 'es') && snip.es">ES: {{ snip['es'] }}</small>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import Btn from '@/components/basic/Btn'
import Field from '@/components/basic/Field'
import mixinLanguages from '@/mixins/languages'
import SearchBox from '@/components/basic/SearchBox'
import ProgressBar from '@/components/basic/ProgressBar'
import { saveAs } from 'file-saver'

export default {
  name: 'Home',
  mixins: [mixinLanguages],
  components: {
    Field,
    Btn,
    SearchBox,
    ProgressBar
  },
  data: () => ({
    searchString: '',
    isLoaded: false,
    dirtySnips: []
  }),
  computed: {
    ...mapState({
      snips: state => state.snips,
      missing: state => state.missing
    }),
    lang () {
      return this.$route.params.lang
    },
    filteredSnips () {
      if (!this.snips || !this.snips.length) { return [] }
      return this.snips.filter(item => {
        if (!this.searchString) {
          return true
        }
        return (item._id.toLowerCase().indexOf(this.searchString.toLowerCase()) > -1) ||
          (item[this.lang].toLowerCase().indexOf(this.searchString.toLowerCase()) > -1)
      })
    },
    remainingPercent () {
      return 100 - Math.round(this.missing.length / (this.snips.length + this.missing.length) * 100)
    }
  },
  beforeMount () {
    window.addEventListener('beforeunload', this.preventNavWithoutSave)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.preventNavWithoutSave)
  },
  beforeRouteLeave (to, from, next) {
    if (this.dirtySnips.length) {
      if (!window.confirm('Leave without saving?')) {
        return
      }
    }
    next()
  },
  async mounted () {
    this.$store.commit('SetLanguageSnips', [])
    this.$store.commit('SetMissingSnips', [])
    await this.languagesGetMissing(this.lang)
    await this.languagesGetOne(this.lang)
    this.isLoaded = true
    this.$refs.showAll.checked = (this.missing.length === 0)
    this.focusMissing()
  },
  methods: {
    preventNavWithoutSave (event) {
      if (!this.dirtySnips.length) return
      event.preventDefault()
      // Chrome requires returnValue to be set.
      event.returnValue = ''
    },
    handleSearch (val) {
      this.searchString = val
      // make sure list is visible when starting search
      if (val) {
        this.$refs.showAll.checked = true
      }
    },
    async saveCurrent () {
      if (this.missing[0][this.lang]) {
        await this.languagesSaveSnip(this.lang, this.missing[0])
        this.$store.commit('SaveNewSnip', this.missing[0])
      }
      this.focusMissing()
    },
    handleChange (el, val) {
      if (this.dirtySnips.indexOf(el) === -1) {
        this.dirtySnips.push(el)
      }
      // console.log('this.dirtySnips', this.dirtySnips)
    },
    focusMissing () {
      if (this.missing.length) {
        setTimeout(() => {
          this.$refs.singleSnip.$el.focus()
        }, 100)
      }
    },
    supplyDefault () {
      this.$set(this.missing[0], this.lang, this.missing[0]._id)
    },
    async saveDirty () {
      await this.languagesSaveMultipleSnips(this.lang, this.dirtySnips)
      this.dirtySnips = []
    },
    async download () {
      // await this.languagesDownloadSnip(this.lang)
      const toSave = {}
      this.snips.forEach(item => {
        toSave[item._id] = item[this.lang]
      })
      // console.log('Saving', JSON.parse(JSON.stringify(toSave)))
      const file = new File(['{' + JSON.stringify(toSave) + '}'], this.lang + '.json', {type: 'application/json'})
      // need to do manual cutting of [] characters forced by the blob
      const fileClone = file.slice(1, file.size - 1)
      // console.log(fileClone, saveAs)
      saveAs(fileClone, this.lang + '.json')
    }
  }
}
</script>

<style scoped lang="scss">
.top-bar {
  border-bottom: 1px solid $gray-light;
  position: fixed;
  background-color: $white;
  z-index: 1;
}
.main-area {
  margin-top: $size-grid;
}
.go-home {
  svg {
    fill: $cyan;
  }

  &:hover svg {
    fill: darken($cyan, 15%);
  }
}
.flag {
  transform: scale(0.75);
  transform-origin: left center;
  margin: 0;
  cursor: default;
}
.label-and-value {
  & + .label-and-value {
    margin-top: $size-grid / 4;
  }

  label div {
    user-select: text;
  }
}
small {
  display: block;
}
.snips-done {
  display: none;
}
#show-translated {
  display: none;
}
#show-translated:checked ~ .snips-done {
  display: block;
}
.toggle-translated {
  margin: 1rem 0;
  padding: 1rem 0;
}
.toggle-translated svg {
  fill: $cyan;
  transform: rotate(-90deg);
  transform-origin: center;
}
.toggle-translated:hover {
  fill: darken($cyan, 15%);
  color: darken($cyan, 15%);
}
#show-translated:checked ~ .toggle-translated svg {
  transform: rotate(90deg);
}
</style>



// WEBPACK FOOTER //
// src/modules/Languages/One.vue